import { createContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import Cookies from "universal-cookie";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { AuthContextType, ContextProviderPropsType } from "../types/context.type";
import { refreshAccessTokenService } from "../utils/api/authentication/refreshAccessToken";
import { signOutUser } from "../utils/api/authentication/signout";
import { retrieveSystemSettingService } from "../utils/api/configuration/systemsettings/retrieveSystemSetting";
import { retrieveUserProfileService } from "../utils/api/usermanagement/profile/retrieveUser";

export const AuthContext = createContext<AuthContextType>({
    isAuthenticated: false,
    setIsAuthenticated: () => { },
    loading: true,
    logout: () => { },
});

export const AuthProvider: React.FC<ContextProviderPropsType> = ({
    children,
}) => {
    const cookies = useMemo(() => new Cookies(), []);
    const TOKEN = cookies.get("TOKEN");
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    // default system timeout is five seconds
    const [systemTimeOut, setSystemTimeOut] = useState(5);

    const apiKeyRef = useRef<string | null>(null);
    const tokenRef = useRef<string>(TOKEN);
    const lastActivityTimeRef = useRef<number | null>(null);
    const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Track user activity only if authenticated
    useEffect(() => {
        if (!isAuthenticated) return;

        const updateActivity = () => {
            lastActivityTimeRef.current = Date.now();
        };

        const events = ["click", "mousemove", "keydown", "scroll"];
        events.forEach((event) =>
            window.addEventListener(event, updateActivity, { passive: true })
        );

        return () => {
            events.forEach((event) =>
                window.removeEventListener(event, updateActivity)
            );
        };
    }, [isAuthenticated]);

    useEffect(() => {
        if (!TOKEN) return;
        const fetchUserProfile = async () => {
            try {
                const user = await retrieveUserProfileService(TOKEN);
                apiKeyRef.current = user?._id;
            } catch (error) {
                console.error("Failed to fetch user profile:", error);
            }
        };
        fetchUserProfile();
    }, [TOKEN]);

    useEffect(() => {
        if (!TOKEN) return;
        retrieveSystemSettingService(TOKEN)
            .then((data) => setSystemTimeOut(data.timeout))
            .catch((err) => {
                console.error("Failed to fetch timeout:", err);
            });
    }, [TOKEN]);

    const clearTimer = () => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
    };

    const handleLogout = useCallback(async () => {
        try {
            const response = await signOutUser(apiKeyRef.current, true);
            if (response.status === "success") {
                setLoading(false);
            } else {
                console.error('Logout failed. Try again');
            }
        } catch (error) {
            console.error(error);
        }
        clearTimer();
        cookies.remove("TOKEN", { path: "/" });
        tokenRef.current = "";
        setIsAuthenticated(false);
        return navigate("/", { replace: true });
    }, [cookies, navigate]);

    const scheduleRefresh = useCallback(() => {
        const token = tokenRef.current;
        if (!token) return;
        try {
            const decoded: { exp?: number } = jwtDecode(token);
            if (!decoded.exp) {
                handleLogout();
                return;
            };

            const tokenExpiryTime = decoded.exp * 1000;
            // token should refresh a minute to it's expiry time.
            const refreshTime = tokenExpiryTime - 60 * 1000;
            const now = Date.now();
            const timeUntilRefresh = refreshTime - now;
            if (timeUntilRefresh <= 0) {
                handleLogout();
                return;
            };

            clearTimer();

            refreshTimerRef.current = setTimeout(async () => {
                // check if user has been active in the last XXX minutes
                // where XXX minutes is configured timeout in minutes
                const now = Date.now();
                const timeSinceActivity = lastActivityTimeRef.current
                    ? now - lastActivityTimeRef.current
                    : Number.POSITIVE_INFINITY;
                const isUserActive = timeSinceActivity < systemTimeOut * 60 * 1000;

                if (isUserActive) {
                    try {
                        const response = await refreshAccessTokenService(tokenRef.current);
                        if (response.status === "success") {
                            cookies.set("TOKEN", response.token, { path: "/" });
                            tokenRef.current = response.token;
                            return scheduleRefresh();
                        } else {
                            handleLogout();
                        }
                    } catch (err) {
                        console.error("Refresh failed:", err);
                        handleLogout();
                    }
                } else {
                    handleLogout();
                }
            }, timeUntilRefresh);
        } catch (err) {
            console.error("Error decoding token:", err);
            handleLogout();
        }
    }, [cookies, systemTimeOut, handleLogout]);

    // Validate token after login and start refresh scheduling
    useEffect(() => {
        const validateAuth = async () => {
            const token = cookies.get("TOKEN");
            if (token) {
                try {
                    const decoded: { exp?: number } = jwtDecode(token);
                    if (!decoded.exp) throw new Error("Invalid token");

                    tokenRef.current = token;
                    setIsAuthenticated(true);
                    lastActivityTimeRef.current = null;
                    scheduleRefresh();
                } catch (err) {
                    console.error("Token decode failed:", err);
                    handleLogout();
                }
            } else {
                setIsAuthenticated(false);
            }
            setLoading(false);
        };
        validateAuth();
    }, [cookies, scheduleRefresh, handleLogout]);

    const contextValue = useMemo(
        () => ({
            isAuthenticated,
            setIsAuthenticated,
            logout: handleLogout,
            loading,
        }),
        [isAuthenticated, handleLogout, loading]
    );

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};
